import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import 'moment-timezone';
import { memo, useEffect, useState } from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { useHistory } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { themeModeAtom, themeModeEnum } from 'src/recoil/atoms/themeModeAtom';
import { LabelledOutline } from '../../../../common/final-form-material-ui';
import { getSearchString } from '../../../../common/helperFunctions/getSearchString';
import { endingDate } from '../../../../recoil/atoms/endingDate';
import { startingDate } from '../../../../recoil/atoms/startingDate';
import { methodNameEnum } from '../../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../../services/rest/makeAPIRequest';

const useStyles = makeStyles((_theme) => ({
  datePicker: {
    '& [aria-label=outline-body-content]': {
      paddingTop: 7,
      paddingBottom: 7,
      marginBottom: 0,
    },
  },
}));

/**
 * Create moment from timezone and datetime
 *
 * NOTE: Date-picker cannot share moment objects
 * and needs a fresh object for each range
 *
 * @returns Moment
 */
export function momentTz(
  datetime?: string | moment.MomentInput,
  timezone?: string | null,
  addAlmostOneDay?: boolean // add 1 day - 1 millisecond so still within day
) {
  const localDatetime = timezone
    ? datetime // NOTE: moment(null) is invalid date
      ? moment.tz(datetime, timezone) // if timezone & datetime not null
      : moment.tz(timezone) // if timezone not null and datetime null
    : moment(datetime ?? undefined); // if timezone and datetime both null
  return addAlmostOneDay
    ? localDatetime.add('hours', 24).subtract('millisecond', 1) // advance one day minus 1 millisecond
    : localDatetime;
}

/**
 * Get Date ranges for Date Picker
 * @returns Time ranges for DateRangePicker
 */
function getRanges() {
  return {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract('days', 1), moment().subtract('days', 1)],
    'Last 7 Days': [moment().subtract('days', 6), moment()],
    'Last 14 Days': [moment().subtract('days', 13), moment()],
    'Last 30 Days': [moment().subtract('days', 29), moment()],
    'Last 60 Days': [moment().subtract('days', 59), moment()],
    //'Last 90 Days': [momentTz().subtract('days', 89), momentTz()],
    'This Week': [moment().startOf('week'), moment().endOf('week')],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Last Month': [
      moment().subtract('month', 1).startOf('month'),
      moment().subtract('month', 1).endOf('month'),
    ],
  };
}

const addQueryParamsIfNeeded = () => {
  const params = new URLSearchParams(window.location.search);
  const start = params.get('start');

  // If there is no query parameter with the value "start" AND there is local storage data for date range, add params to the URL
  if (!start && window.sessionStorage.getItem('dateRange')) {
    const newUrl =
      window.location.href +
      (window.location.search === '' ? '?' : '&') +
      window.sessionStorage.getItem('dateRange');

    window.history.pushState({ path: newUrl }, '', newUrl);
  }
};

/**
 * Date picker uses moment to set timezone in business timezone
 * Sets the startingDate and endingDate atom
 */
export function DatePicker() {
  const history = useHistory();
  const classes = useStyles();

  const [
    businessTimezoneCached,
    businessTimezoneLoadable,
  ] = makeApiRequestCached<string | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0]?.timezone_php ??
      cached.current
  );

  useEffect(() => {
    const urlSearchParams = getSearchString();
    // only fix startingDate and endingDate only when tz is not in url
    // set midnight from business timezone
    if (!urlSearchParams.get('tz') && businessTimezoneCached.current) {
      const tzUrl = urlSearchParams.get('tz') ?? businessTimezoneCached.current;
      const startUrl = urlSearchParams.get('start');
      const endUrl = urlSearchParams.get('end');

      setStarting(
        startUrl
          ? momentTz(startUrl, tzUrl)
          : momentTz(moment().format('YYYY-MM-DD'), tzUrl).subtract('days', 29)
      );

      setEnding(
        momentTz(
          endUrl ?? moment().format('YYYY-MM-DD'),
          tzUrl,
          startUrl !== undefined && startUrl === endUrl
        )
      );
    }
  }, [businessTimezoneLoadable]);

  const [starting, setStarting] = useRecoilState(startingDate);
  const [ending, setEnding] = useRecoilState(endingDate);
  const [ranges, setRanges] = useState<ReturnType<typeof getRanges>>(
    getRanges()
  );
  const themeMode = useRecoilValue(themeModeAtom);

  useEffect(() => {
    addQueryParamsIfNeeded();
  }, [window.location.pathname]);

  /**
   * Update url query and set start/end atom
   * @param {Moment} start
   * @param {Moment} end
   */
  function handleCallback(start: moment.Moment, end: moment.Moment) {
    // date picker will return browser midnight
    // so convert to business location midnight
    const localStart = momentTz(
      start.format('YYYY-MM-DDTHH:mm:ss.SSS'), // output midnight local time (12:00:00)
      businessTimezoneCached.current // set timezone to be business timezone
    );
    // advance by almost a day of start = end
    const localEnd = momentTz(
      end.format('YYYY-MM-DDTHH:mm:ss.SSS'), // output midnight local time (12:00:00)
      businessTimezoneCached.current // set timezone to be business timezone
    );

    const search = new URLSearchParams(window.location.search);
    search.set('start', localStart.format('YYYY-MM-DD')); // local day
    search.set('end', localEnd.format('YYYY-MM-DD')); // local day
    search.set('tz', String(businessTimezoneCached.current));

    const storage = window.sessionStorage;

    const searchStringForStorage = `start=${localStart.format(
      'YYYY-MM-DD'
    )}&end=${localEnd.format('YYYY-MM-DD')}&tz=${String(
      businessTimezoneCached.current
    )}`;
    storage.setItem('dateRange', searchStringForStorage);

    setStarting(localStart);
    setEnding(localEnd);

    history.push({ search: search.toString() });
  }

  useEffect(() => {
    /**
     * Check if next date then update date picker
     */
    const timer = setInterval(() => {
      // if next day then update
      if (
        ranges?.Today[0].format('YYYY-MM-DD') !== moment().format('YYYY-MM-DD')
      ) {
        // generate and set new dates in state
        setRanges(getRanges());
      }
      //every hour
    }, 60_000);

    // clearing interval on unload of component
    return () => clearInterval(timer);
  }, []);

  /**
   * Memo since ranges update will force re-render
   */
  const DateRangePickerMemo = memo(() => (
    <DateRangePicker
      initialSettings={{
        startDate: starting, // moment(starting).tz(businessTimezoneCached.current),
        endDate: ending, // moment(ending).tz(businessTimezoneCached.current),
        ranges,
      }}
      onCallback={handleCallback}
    >
      <div>
        <i className="fa fa-calendar" />{' '}
        <span>
          {`${starting.format('MMM Do YYYY')} - ${ending.format(
            'MMM Do YYYY'
          )}`}
        </span>
        <i className="fa fa-caret-down" />
      </div>
    </DateRangePicker>
  ));

  return (
    <LabelledOutline
      label={businessTimezoneCached.current ?? 'Date Range'}
      className={classes.datePicker}
      customPadding="7px 14px"
      backgroundColor={
        themeMode === themeModeEnum.DARK_MODE ? '#121c21' : '#fff'
      }
    >
      <DateRangePickerMemo />
    </LabelledOutline>
  );
}
